/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 프로그래스바 클래스를 정의한다.
 *
 * @file /src/components/progress/Progress.ts
 * @author Arzz <arzz@arzz.com>
 * @license MIT License
 * @modified 2026. 5. 28.
 */
import * as parent from '@base/Component';
import { Html } from '@utils/Html';
import { Dom } from '@utils/Dom';
import { LanguageText } from '@utils/Language';
import './Progress.css';

export interface Listeners<T> extends parent.Listeners<T> {}

export interface Properties<T> extends parent.Properties<T> {
    /**
     * 프로그래스바 메시지
     */
    message?: LanguageText;

    /**
     * 프로그래스바 시작점
     */
    min?: number;

    /**
     * 프로그래스바 종료점
     */
    max?: number;

    /**
     * 프로그래스바 값
     */
    value?: number;

    /**
     * 로딩상태여부
     */
    loading?: boolean;

    /**
     * 이벤트리스너
     */
    listeners?: Listeners<T>;
}

export class Progress extends parent.Component {
    /**
     * 컴포넌트 타입
     */
    type = 'progress';

    /**
     * 컴포넌트 확장타입
     */
    variant = 'progress';

    /**
     * 프로그래스바 메시지
     */
    message: LanguageText = null;

    /**
     * 프로그래스바 시작점
     */
    min: number = 0;

    /**
     * 프로그래스바 종료점
     */
    max: number = 100;

    /**
     * 프로그래스바 값
     */
    value: number = 0;

    /**
     * 로딩상태여부
     */
    loading: boolean = false;

    /**
     * 메시지 DOM
     */
    $message: Dom = null;

    /**
     * 프로그래스 DOM
     */
    $progress: Dom = null;

    /**
     * 프로그래스바 클래스를 정의한다.
     *
     * @param properties - 초기설정
     */
    constructor(public properties: Properties<Progress> = null) {
        super(properties);

        this.message = this.properties?.message ?? null;
        this.min = this.properties?.min ?? 0;
        this.max = this.properties?.max ?? 100;
        this.value = this.properties?.value ?? 0;
        this.loading = this.properties?.loading == true;
        this.scrollable = false;
    }

    /**
     * 메시지 DOM 을 가져온다.
     *
     * @returns 메시지 DOM
     */
    $getMessage(): Dom {
        if (this.$message === null) {
            this.$message = Html.create('div', { 'data-role': 'message' });
        }

        return this.$message;
    }

    /**
     * 프로그래스바 DOM 을 가져온다.
     *
     * @returns 프로그래스바 DOM
     */
    $getProgress(): Dom {
        if (this.$progress === null) {
            this.$progress = Html.create('progress', {
                min: this.min.toString(),
                max: Math.max(this.max, 1).toString(),
            });
        }

        return this.$progress;
    }

    /**
     * 프로그래스바 메시지를 변경한다.
     *
     * @param message - 변경할 메시지
     */
    setMessage(message: LanguageText = null): void {
        this.message = message;
        this.$getMessage().html(this.message ?? '');
    }

    /**
     * 프로그래스바 값을 변경한다.
     *
     * @param value - 프로그래스바 값
     */
    setValue(value: number): void {
        if (this.loading == true) {
            this.setLoading(false);
        }
        this.value = value;
        this.$getProgress().setAttr('value', (this.max === 0 ? 1 : this.value).toString());
    }

    /**
     * 프로그래스바 시작점을 변경한다.
     *
     * @param min - 프로그래스바 시작점
     */
    setMin(min: number): void {
        this.min = min;
        this.$getProgress().setAttr('min', this.min.toString());
    }

    /**
     * 프로그래스바 종료점을 변경한다.
     *
     * @param max - 프로그래스바 종료점
     */
    setMax(max: number): void {
        this.max = max;
        this.$getProgress().setAttr('max', Math.max(this.max, 1).toString());
    }

    /**
     * 프로그래스바 값을 가져온다.
     *
     * @returns 프로그래스바 값
     */
    getValue(): number {
        return this.value;
    }

    /**
     * 프로그래스바 로딩상태를 변경한다.
     *
     * @param loading - 로딩상태여부
     */
    setLoading(loading: boolean): void {
        this.loading = loading;
        if (this.loading == true) {
            this.$getProgress().addClass('loading');
        } else {
            this.$getProgress().removeClass('loading');
        }
    }

    /**
     * 컴포넌트 컨테이너를 렌더링한다.
     *
     * @param is_force - 렌더링이 당장 필요없는 상태라도, 강제로 렌더링할지 여부
     */
    async renderContainer(is_force: boolean = false): Promise<void> {
        const $message = this.$getMessage();
        $message.html(this.message ?? '');
        this.$getContent().append($message);

        const $progress = this.$getProgress();
        $progress.setAttr('value', this.value.toString());

        if (this.loading == true) {
            $progress.addClass('loading');
        }

        this.$getContent().append($progress);

        this.$getContainer().append(this.$getContent());
        await this.renderItems(is_force);
    }

    /**
     * 컴포넌트 렌더링이 완료된 이후 초기상태정의가 필요할 경우, 상태를 렌더링한다.
     */
    async renderState(): Promise<void> {
        await super.renderState();
    }

    /**
     * 컴포넌트를 제거한다.
     *
     * @param removeDom - 컴포넌트 DOM 을 제거할지 여부 (기본값 : true)
     */
    async remove(removeDom: boolean = true): Promise<void> {
        this.$message = null;
        this.$progress = null;
        await super.remove(removeDom);
    }
}
